import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import AgentHub from "@/pages/AgentHub";
import Chat from "@/pages/Chat";
import Deliverables from "@/pages/Deliverables";
import Framework from "@/pages/Framework";
import Workspaces from "@/pages/Workspaces";
import WorkspaceDetail from "@/pages/WorkspaceDetail";
import WorkspaceMetadata from "@/pages/WorkspaceMetadata";
import WorkspaceMetadataDashboard from "@/pages/WorkspaceMetadataDashboard";
import WorkspaceMetadataOpportunities from "@/pages/WorkspaceMetadataOpportunities";
import WorkspaceMetadataImpact from "@/pages/WorkspaceMetadataImpact";
import WorkspaceSourceParser from "@/pages/WorkspaceSourceParser";
import WorkspaceImportHistory from "@/pages/WorkspaceImportHistory";
import WorkspaceDispositions from "@/pages/WorkspaceDispositions";
import WorkspaceFieldDependencies from "@/pages/WorkspaceFieldDependencies";
import WorkspaceScreenBindings from "@/pages/WorkspaceScreenBindings";
import WorkspaceScreenWalkthrough from "@/pages/WorkspaceScreenWalkthrough";
import WorkspaceBusinessRules from "@/pages/WorkspaceBusinessRules";
import WorkspaceRiskBrief from "@/pages/WorkspaceRiskBrief";
import WorkspaceSapEcc from "@/pages/WorkspaceSapEcc";
import Findings from "@/pages/Findings";
import SharedWorkspace from "@/pages/SharedWorkspace";
import SharedHeatmap from "@/pages/SharedHeatmap";
import SharedBundle from "@/pages/SharedBundle";
import Telemetry from "@/pages/Telemetry";

/** Tiny helper that wraps a page in <ProtectedRoute>. */
const Protected = (el) => <ProtectedRoute>{el}</ProtectedRoute>;

function Shell() {
  const location = useLocation();
  // Shared workspace links are rendered without the global layout — they're
  // intentionally public (client briefings). Iter 31 adds /share/heatmap/:token
  // for the standalone heat-map share viewer; route order matters because the
  // more specific pattern must match first.
  if (location.pathname.startsWith("/share/")) {
    return (
      <Routes>
        <Route path="/share/heatmap/:token" element={<SharedHeatmap />} />
        <Route path="/share/bundle/:token" element={<SharedBundle />} />
        <Route path="/share/:token" element={<SharedWorkspace />} />
      </Routes>
    );
  }
  return (
    <Layout>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/accept" element={<AcceptInvite />} />
        <Route path="/framework" element={<Framework />} />

        {/* Authenticated routes */}
        <Route path="/hub" element={Protected(<AgentHub />)} />
        <Route path="/chat/:agentId" element={Protected(<Chat />)} />
        <Route path="/deliverables" element={Protected(<Deliverables />)} />
        <Route path="/workspaces" element={Protected(<Workspaces />)} />
        <Route path="/workspaces/:id" element={Protected(<WorkspaceDetail />)} />
        <Route path="/workspaces/:id/metadata" element={Protected(<WorkspaceMetadata />)} />
        <Route path="/workspaces/:id/metadata/dashboard" element={Protected(<WorkspaceMetadataDashboard />)} />
        <Route path="/workspaces/:id/metadata/opportunities" element={Protected(<WorkspaceMetadataOpportunities />)} />
        <Route path="/workspaces/:id/metadata/impact" element={Protected(<WorkspaceMetadataImpact />)} />
        <Route path="/workspaces/:id/metadata/source-parser" element={Protected(<WorkspaceSourceParser />)} />
        <Route path="/workspaces/:id/metadata/imports" element={Protected(<WorkspaceImportHistory />)} />
        <Route path="/workspaces/:id/metadata/field-dependencies" element={Protected(<WorkspaceFieldDependencies />)} />
        <Route path="/workspaces/:id/metadata/screen-bindings" element={Protected(<WorkspaceScreenBindings />)} />
        <Route path="/workspaces/:id/metadata/screen-walkthrough" element={Protected(<WorkspaceScreenWalkthrough />)} />
        <Route path="/workspaces/:id/dispositions" element={Protected(<WorkspaceDispositions />)} />
        <Route path="/workspaces/:id/business-rules" element={Protected(<WorkspaceBusinessRules />)} />
        <Route path="/workspaces/:id/risk-brief" element={Protected(<WorkspaceRiskBrief />)} />
        <Route path="/workspaces/:id/sap-ecc" element={Protected(<WorkspaceSapEcc />)} />
        <Route path="/findings" element={Protected(<Findings />)} />
        <Route path="/telemetry" element={Protected(<Telemetry />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

// Module-level so the Toaster doesn't get a fresh object identity on every
// re-render of <App />. Sonner shallow-compares this prop and re-applies
// styles when it changes, which means a stable reference matters here.
const TOAST_OPTIONS = {
  style: {
    background: "#161616",
    color: "#f4f4f4",
    border: "1px solid #393939",
    borderRadius: "2px",
    fontFamily: '"IBM Plex Sans", sans-serif',
  },
  className: "ml-toast",
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" toastOptions={TOAST_OPTIONS} />
    </div>
  );
}

export default App;
